import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { updateMetaTags, defaultMeta } from '../utils/metaTags';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiCalculator, FiTarget, FiShield, FiZap } = FiIcons;

const About = () => {
  useEffect(() => {
    updateMetaTags({
      title: 'About Us | UK Salary Toolkit - Free Tax Calculator',
      description: 'Learn about UK Salary Toolkit - providing free, accurate UK tax calculators for 2025/26. Calculate take-home pay, student loans, and more.',
      canonical: `${defaultMeta.siteUrl}/about`,
    });
  }, []);

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      <motion.section 
        className="text-center space-y-4"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <h1 className="text-4xl md:text-5xl font-bold text-gray-900">About UK Salary Toolkit</h1>
        <p className="text-lg text-gray-600">
          Free, accurate UK tax calculators designed for simplicity and speed
        </p>
      </motion.section>

      <motion.section 
        className="bg-white rounded-xl p-6 md:p-8 shadow-sm border"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
      >
        <h2 className="text-2xl font-semibold text-gray-900 mb-4">Our Mission</h2>
        <p className="text-gray-700 mb-4">
          UK Salary Toolkit was created to provide free, easy-to-use tax calculation tools for UK residents. We believe everyone should have quick access to accurate salary and tax information without complicated forms or hidden fees.
        </p>
        <p className="text-gray-700">
          Our calculators use official HMRC rates for the 2025/26 tax year and are updated regularly to ensure accuracy. All calculations are performed locally in your browser - we never see or store your personal financial information.
        </p>
      </motion.section>

      <motion.section 
        className="grid md:grid-cols-2 gap-6"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        <div className="bg-blue-50 rounded-xl p-6 border border-blue-100">
          <div className="flex items-center gap-3 mb-4">
            <div className="bg-blue-600 p-3 rounded-lg text-white">
              <SafeIcon icon={FiCalculator} size={24} />
            </div>
            <h3 className="text-xl font-semibold text-gray-900">Accurate Calculations</h3>
          </div>
          <p className="text-gray-700">
            Based on official HMRC tax rates and thresholds for 2025/26. Includes Income Tax, National Insurance, Student Loans, and Pension contributions.
          </p>
        </div>

        <div className="bg-indigo-50 rounded-xl p-6 border border-indigo-100">
          <div className="flex items-center gap-3 mb-4">
            <div className="bg-indigo-600 p-3 rounded-lg text-white">
              <SafeIcon icon={FiZap} size={24} />
            </div>
            <h3 className="text-xl font-semibold text-gray-900">Fast & Simple</h3>
          </div>
          <p className="text-gray-700">
            No sign-ups, no forms, no waiting. Enter your salary and get instant results. All calculations happen in your browser for maximum privacy.
          </p>
        </div>

        <div className="bg-emerald-50 rounded-xl p-6 border border-emerald-100">
          <div className="flex items-center gap-3 mb-4">
            <div className="bg-emerald-600 p-3 rounded-lg text-white">
              <SafeIcon icon={FiShield} size={24} />
            </div>
            <h3 className="text-xl font-semibold text-gray-900">100% Private</h3>
          </div>
          <p className="text-gray-700">
            Your data never leaves your device. We don't collect, store, or transmit any salary information you enter. Complete privacy guaranteed.
          </p>
        </div>

        <div className="bg-amber-50 rounded-xl p-6 border border-amber-100">
          <div className="flex items-center gap-3 mb-4">
            <div className="bg-amber-600 p-3 rounded-lg text-white">
              <SafeIcon icon={FiTarget} size={24} />
            </div>
            <h3 className="text-xl font-semibold text-gray-900">Comprehensive Tools</h3>
          </div>
          <p className="text-gray-700">
            Calculate take-home pay, convert salary to hourly rates, estimate student loan repayments, and compare salaries across different regions.
          </p>
        </div>
      </motion.section>

      <motion.section 
        className="bg-white rounded-xl p-6 md:p-8 shadow-sm border"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
      >
        <h2 className="text-2xl font-semibold text-gray-900 mb-4">What We Calculate</h2>
        <p className="text-gray-700 mb-6 leading-relaxed">
          Our calculators provide comprehensive UK tax calculations using official HMRC rates and thresholds for the 2025/26 tax year. All calculations are performed locally in your browser for complete privacy and security.
        </p>
        <div className="grid md:grid-cols-2 gap-6 text-gray-700">
          <div>
            <h3 className="font-semibold mb-3 text-gray-900">Core Tax Calculations:</h3>
            <ul className="space-y-2">
              <li className="flex items-start gap-2">
                <span className="text-blue-600 font-bold">•</span>
                <span><strong>Income Tax:</strong> All UK regions including Scotland with accurate band calculations and personal allowance taper</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-600 font-bold">•</span>
                <span><strong>National Insurance:</strong> Class 1 employee contributions with 2025/26 rates (8% and 2% bands)</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-600 font-bold">•</span>
                <span><strong>Student Loan Repayments:</strong> All UK plans (1, 2, 4, 5) and Postgraduate loans with correct thresholds</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-600 font-bold">•</span>
                <span><strong>Pension Contributions:</strong> Auto-enrolment and voluntary contributions with tax relief calculations</span>
              </li>
            </ul>
          </div>
          <div>
            <h3 className="font-semibold mb-3 text-gray-900">Additional Features:</h3>
            <ul className="space-y-2">
              <li className="flex items-start gap-2">
                <span className="text-blue-600 font-bold">•</span>
                <span><strong>Take-Home Pay:</strong> Monthly, weekly, and annual net pay calculations</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-600 font-bold">•</span>
                <span><strong>Salary Conversions:</strong> Annual to hourly rate conversions with customizable working hours</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-600 font-bold">•</span>
                <span><strong>Regional Comparisons:</strong> Side-by-side England/Wales/NI vs Scotland tax calculations</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-600 font-bold">•</span>
                <span><strong>Detailed Breakdowns:</strong> Complete tax breakdowns showing each deduction and percentage</span>
              </li>
            </ul>
          </div>
        </div>
      </motion.section>

      <motion.section 
        className="bg-gradient-to-br from-slate-50 to-blue-50 rounded-xl p-6 md:p-8 shadow-sm border border-slate-200"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
      >
        <h2 className="text-2xl font-semibold text-gray-900 mb-4">Understanding UK Tax System</h2>
        <div className="space-y-6 text-gray-700">
          <div>
            <h3 className="font-semibold mb-2 text-gray-900">How UK Income Tax Works</h3>
            <p className="mb-3 leading-relaxed text-sm">
              UK income tax is progressive, meaning you pay different rates on different portions of your income. Everyone gets a personal allowance (£12,570 for 2025/26) which is tax-free. Income above this is taxed at increasing rates: 20% (basic), 40% (higher), and 45% (additional rate).
            </p>
            <p className="leading-relaxed text-sm">
              Scotland has its own tax rates and bands, which can result in different take-home pay compared to England, Wales, and Northern Ireland. Our calculator accounts for all regional differences.
            </p>
          </div>
          <div>
            <h3 className="font-semibold mb-2 text-gray-900">National Insurance Explained</h3>
            <p className="mb-3 leading-relaxed text-sm">
              National Insurance (NI) is separate from income tax and funds state benefits including the State Pension. For employees, NI is calculated on weekly earnings. The rates were reduced in 2024 from 12% to 8% on the main band, providing significant savings for most workers.
            </p>
            <p className="leading-relaxed text-sm">
              Unlike income tax, NI has a weekly calculation basis, which our calculator accurately handles. There's no NI on earnings below £12,570, then 8% up to £50,270, and 2% above that.
            </p>
          </div>
          <div>
            <h3 className="font-semibold mb-2 text-gray-900">Student Loan Repayment System</h3>
            <p className="mb-3 leading-relaxed text-sm">
              UK student loans work differently from traditional loans. Repayments are income-contingent, meaning you only pay when you earn above a threshold. The repayment is a percentage of your income above the threshold, not a fixed amount. Most loans are written off after 25-40 years, depending on the plan.
            </p>
            <p className="leading-relaxed text-sm">
              Many graduates will never fully repay their student loans before they're written off, which is why overpaying isn't always financially beneficial. Our calculator helps you understand exactly how much you'll repay based on your salary.
            </p>
          </div>
          <div>
            <h3 className="font-semibold mb-2 text-gray-900">Tax Efficiency and Planning</h3>
            <p className="mb-3 leading-relaxed text-sm">
              Understanding your tax situation helps with financial planning. Higher rate taxpayers (earning over £50,270) get 40% tax relief on pension contributions, making pensions highly tax-efficient. The personal allowance taper above £100,000 creates a 60% effective tax rate, which many people avoid by increasing pension contributions.
            </p>
            <p className="leading-relaxed text-sm">
              Our calculators show you exactly how different scenarios (pension contributions, student loans, regional differences) affect your take-home pay, helping you make informed financial decisions.
            </p>
          </div>
        </div>
      </motion.section>

      <motion.section 
        className="bg-gradient-to-br from-blue-600 to-indigo-600 rounded-xl p-6 md:p-8 text-white"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
      >
        <h2 className="text-2xl font-semibold mb-4">Important Disclaimer</h2>
        <p className="mb-4 opacity-90">
          UK Salary Toolkit provides estimates based on standard UK tax rates. Our calculators are for informational and educational purposes only and do not constitute financial, tax, or legal advice.
        </p>
        <p className="mb-4 opacity-90">
          Actual tax deductions may vary based on individual circumstances, tax codes, benefits in kind, and other factors. Always consult with HMRC or a qualified tax advisor for advice specific to your situation.
        </p>
        <p className="opacity-90">
          Tax rates are based on the 2025/26 tax year and may be subject to change. We strive to keep information accurate but cannot guarantee it is always current.
        </p>
      </motion.section>

      <motion.section 
        className="bg-white rounded-xl p-6 md:p-8 shadow-sm border text-center"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
      >
        <h2 className="text-2xl font-semibold text-gray-900 mb-4">Ready to Calculate?</h2>
        <p className="text-gray-700 mb-6">
          Start using our free UK tax calculator tools today
        </p>
        <Link
          to="/"
          className="inline-block bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
        >
          Calculate Your Take-Home Pay
        </Link>
      </motion.section>
    </div>
  );
};

export default About;

